import React from 'react';
import { fechaSegura } from '../../utils/formatos';

import {
    Close as CloseIcon,
    Print as PrintIcon,
    MonitorHeart as VitalsIcon,
    Assignment as NoteIcon,
    Science as ScienceIcon,
    Healing as HealingIcon,
    Person as PersonIcon,
    EventNote as EventIcon
} from '@mui/icons-material';

const ModalInformeMedico = ({ consulta, paciente, onClose }) => {
    if (!consulta || !paciente) return null;

    const safeFormatDate = (dateString, formatStr) => fechaSegura(dateString, formatStr, '--');

    const handlePrint = () => {
        window.print();
    };

    const SeccionTexto = ({ titulo, texto, icono }) => {
        if (!texto) return null;
        return (
            <div className="mb-6 break-inside-avoid">
                <h4 className="flex items-center gap-2 text-sm font-extrabold text-clinica-primary uppercase mb-2">
                    {icono} {titulo}
                </h4>
                <p className="whitespace-pre-line leading-relaxed pl-4 border-l-4 border-slate-200 text-slate-700 text-sm font-medium">
                    {texto}
                </p>
            </div>
        );
    };

    return (
        // Añadimos print:hidden al overlay para que el fondo gris desaparezca en el PDF
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in print:bg-transparent print:p-0 print:backdrop-blur-none">

            <div className="w-full max-w-4xl bg-slate-50 rounded-3xl shadow-2xl flex flex-col max-h-[95vh] print:max-h-none print:shadow-none print:border-none print:bg-white printable-dialog">
                {/* CABECERA */}
                <div className="flex items-start justify-between p-6 bg-white border-b border-slate-200 rounded-t-3xl print:rounded-none">
                    <div>
                        <h2 className="text-2xl font-black text-slate-900 tracking-tight">
                            Informe Clínico del Acto Médico
                        </h2>
                        <div className="flex items-center gap-2 mt-1.5 text-sm font-bold text-slate-500">
                            <span>ID Consulta: {consulta.id?.slice(-8).toUpperCase() || '--'}</span>
                            <span>•</span>
                            <span>Estado:</span>
                            <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-full bg-slate-100 text-slate-600 print:border print:border-slate-300">
                                {consulta.estado || 'No definido'}
                            </span>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 print:hidden">
                        <button
                            onClick={handlePrint}
                            className="p-2 text-clinica-primary bg-clinica-primary/10 hover:bg-clinica-primary hover:text-white rounded-xl transition-colors focus:outline-none"
                            title="Imprimir o Exportar PDF"
                        >
                            <PrintIcon />
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 text-red-500 bg-red-50 hover:bg-red-500 hover:text-white rounded-xl transition-colors focus:outline-none"
                            title="Cerrar"
                        >
                            <CloseIcon />
                        </button>
                    </div>
                </div>

                {/* CUERPO DEL INFORME (Corrección de Scroll para impresión) */}
                {/* 🚀 print:overflow-visible es vital para que genere múltiples páginas en el PDF */}
                <div className="flex-1 overflow-y-auto p-6 md:p-8 print:overflow-visible print:p-2 bg-white">

                    {/* Tarjetas de Identificación */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 p-6 mb-8 bg-white border border-slate-200 rounded-2xl shadow-sm print:shadow-none print:break-inside-avoid">
                        <div>
                            <h3 className="flex items-center gap-1.5 text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">
                                <PersonIcon fontSize="small" /> Datos del Paciente
                            </h3>
                            <p className="text-lg font-black text-slate-900">{paciente.nombre} {paciente.apellidos}</p>
                            <p className="text-sm font-bold text-slate-500 mt-1">DNI: {paciente.dni} • Tel: {paciente.telefono || '--'}</p>
                        </div>
                        <div>
                            <h3 className="flex items-center gap-1.5 text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">
                                <EventIcon fontSize="small" /> Detalles de Consulta
                            </h3>
                            <p className="text-lg font-black text-slate-900">
                                {safeFormatDate(consulta.fecha, "EEEE, d 'de' MMMM 'de' yyyy • HH:mm")}
                            </p>
                            <p className="text-sm font-bold text-slate-500 mt-1">
                                Profesional: {consulta.profesional?.nombre || 'No asignado'}
                            </p>
                        </div>
                    </div>

                    {/* Triage y Constantes Vitales */}
                    {consulta.constantesVitales && Object.keys(consulta.constantesVitales).length > 0 && (
                        <div className="mb-8 print:break-inside-avoid">
                            <h3 className="flex items-center gap-2 text-sm font-extrabold text-slate-700 uppercase mb-4">
                                <VitalsIcon fontSize="small" className="text-red-500" />
                                Constantes Vitales y Biometría
                            </h3>
                            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3">
                                {Object.entries(consulta.constantesVitales).map(([key, value]) => {
                                    if (!value) return null;
                                    const labels = { tension: 'T. Arterial', ritmoCardiaco: 'FC (lpm)', temperatura: 'Temp (°C)', saturacionO2: 'Sat O2 (%)', peso: 'Peso (kg)', altura: 'Altura (cm)' };
                                    return (
                                        <div key={key} className="p-3 bg-slate-100/80 border border-slate-200 rounded-xl text-center">
                                            <span className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
                                                {labels[key] || key}
                                            </span>
                                            <span className="block text-lg font-black text-slate-900">
                                                {value}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Anamnesis */}
                    <div className="mb-6">
                        <SeccionTexto titulo="Motivo de la Consulta" texto={consulta.motivo} icono={<NoteIcon fontSize="small" />} />
                        <SeccionTexto titulo="Enfermedad Actual / Evolución" texto={consulta.enfermedadActual || consulta.sintomas} icono={<NoteIcon fontSize="small" />} />
                        <SeccionTexto titulo="Antecedentes Recientes" texto={consulta.antecedentesRecientes} icono={<NoteIcon fontSize="small" />} />
                        <SeccionTexto titulo="Exploración Física" texto={consulta.exploracionFisica} icono={<ScienceIcon fontSize="small" />} />
                    </div>

                    {/* Diagnóstico */}
                    {consulta.diagnostico && (
                        <div className="p-6 mb-8 bg-sky-50 border-2 border-sky-400 rounded-2xl print:break-inside-avoid">
                            <h4 className="text-xs font-black text-sky-600 uppercase tracking-widest mb-2">
                                Diagnóstico Principal
                            </h4>
                            <p className="text-lg font-black text-sky-950 leading-snug">
                                {consulta.diagnostico}
                            </p>
                            {consulta.diagnosticosSecundarios && (
                                <p className="text-sm font-semibold text-sky-800 mt-2">
                                    <strong className="font-black">Secundarios:</strong> {consulta.diagnosticosSecundarios}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Tratamiento y Recetas */}
                    <div>
                        <SeccionTexto titulo="Plan de Tratamiento" texto={consulta.tratamiento} icono={<HealingIcon fontSize="small" />} />
                        <SeccionTexto titulo="Pruebas Complementarias" texto={consulta.pruebasComplementarias} icono={<ScienceIcon fontSize="small" />} />

                        {Array.isArray(consulta.recetas) && consulta.recetas.length > 0 && (
                            <div className="mt-6 print:break-inside-avoid">
                                <h4 className="flex items-center gap-2 text-sm font-extrabold text-clinica-primary uppercase mb-3">
                                    <HealingIcon fontSize="small" /> Prescripción Farmacológica
                                </h4>
                                <ul className="space-y-2 pl-4">
                                    {consulta.recetas.map((r, i) => (
                                        <li key={i} className="text-sm font-medium text-slate-700 flex items-start gap-2">
                                            <span className="w-1.5 h-1.5 mt-1.5 rounded-full bg-clinica-primary flex-shrink-0"></span>
                                            <span>
                                                <strong className="font-extrabold text-slate-900">{r.medicamento}</strong>
                                                <span className="mx-2 text-slate-400">—</span>
                                                {r.dosis} <em className="text-slate-500">({r.duracion})</em>
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>

                {/* FOOTER DEL MODAL */}
                <div className="p-4 bg-white border-t border-slate-200 rounded-b-3xl flex justify-center print:hidden">
                    <button
                        onClick={onClose}
                        className="px-8 py-3 text-sm font-bold text-white bg-slate-900 hover:bg-slate-800 rounded-xl transition-colors shadow-sm"
                    >
                        Cerrar Informe
                    </button>
                </div>
            </div>

            {/* CSS RESIDUAL DE IMPRESIÓN (Apoyo a Tailwind) */}
            <style>
                {`
                @media print {
                    body * { visibility: hidden; }
                    .printable-dialog, .printable-dialog * { visibility: visible; }
                    .printable-dialog { 
                        position: absolute; 
                        left: 0; 
                        top: 0; 
                        width: 100%; 
                    }
                }
                `}
            </style>
        </div>
    );
};

export default ModalInformeMedico;